import { useEffect, useState } from 'react';

import { ApiError } from '../../api/client';
import type { ScenarioDto, SimulateResult, ValidateResult } from '../../api/types';
import {
  useActivateVersion,
  useCreateVersion,
  useDisableVersion,
  useScenarioVersion,
  useSimulateVersion,
  useValidateVersion,
} from '../../hooks/use-scenarios';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useToast } from '../ui/toast';
import { ScenarioForm } from './ScenarioForm';
import { EMPTY_DRAFT, type ScenarioDraft } from './scenario-form/types';
import { ScenarioGraph } from './ScenarioGraph';

const TEMPLATE = JSON.stringify(EMPTY_DRAFT, null, 2);

/** D17: a version is activated only after 5 sandbox runs without guardrail failures. */
const MIN_CLEAN_RUNS = 5;

export interface ScenarioEditorProps {
  scenario: ScenarioDto;
}

interface ParsedDraft {
  ok: true;
  definition: ScenarioDraft;
  entryStage?: string;
  stages: { id: string; transitions?: { to: string; when: string }[] }[];
}
interface InvalidDraft {
  ok: false;
  error: string;
}

function parseDraft(text: string): ParsedDraft | InvalidDraft {
  try {
    const definition = JSON.parse(text) as ScenarioDraft;
    const stages = Array.isArray(definition.stages) ? (definition.stages as ParsedDraft['stages']) : [];
    return { ok: true, definition, entryStage: definition.entry_stage, stages };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Invalid JSON' };
  }
}

export function ScenarioEditor({ scenario }: ScenarioEditorProps) {
  const latestVersion = scenario.versions.at(-1);
  const [text, setText] = useState(TEMPLATE);
  const [savedVersion, setSavedVersion] = useState<number | null>(latestVersion?.version ?? null);
  const [validateResult, setValidateResult] = useState<ValidateResult | null>(null);
  const [simulateResult, setSimulateResult] = useState<SimulateResult | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [advanced, setAdvanced] = useState(false);

  // Reset to this scenario's latest version whenever the selected scenario changes.
  useEffect(() => {
    setSavedVersion(latestVersion?.version ?? null);
    setValidateResult(null);
    setSimulateResult(null);
    setActivateError(null);
    if (!latestVersion) setText(TEMPLATE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario.id]);

  const existingVersion = useScenarioVersion(scenario.id, latestVersion ? latestVersion.version : null);
  useEffect(() => {
    if (existingVersion.data && existingVersion.data.version === latestVersion?.version) {
      setText(JSON.stringify(existingVersion.data.definition, null, 2));
      setSimulateResult(
        existingVersion.data.simulation_results ? { runs: existingVersion.data.simulation_results, clean_runs: 0, total_runs: existingVersion.data.simulation_results.length } : null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingVersion.data]);

  const createVersion = useCreateVersion();
  const validateVersion = useValidateVersion();
  const simulateVersion = useSimulateVersion();
  const activateVersion = useActivateVersion();
  const disableVersion = useDisableVersion();
  const toast = useToast();

  const draft = parseDraft(text);
  // the server reports clean_runs; a version loaded from the API carries only its runs
  const cleanRuns = simulateResult
    ? Math.max(simulateResult.clean_runs, simulateResult.runs.filter((r) => !r.guardrailFail && r.turns > 0).length)
    : 0;

  function saveVersion() {
    if (!draft.ok) return;
    createVersion.mutate(
      { scenarioId: scenario.id, definition: draft.definition },
      {
        onSuccess: (res) => {
          setSavedVersion(res.version);
          setValidateResult(null);
          setSimulateResult(null);
          setActivateError(null);
          toast.push(`Версия ${res.version} сохранена как черновик`, 'success');
        },
        onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось сохранить версию'),
      },
    );
  }

  function runValidate() {
    if (savedVersion === null) return;
    validateVersion.mutate(
      { scenarioId: scenario.id, version: savedVersion },
      {
        onSuccess: setValidateResult,
        onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось выполнить валидацию'),
      },
    );
  }

  function runSimulate() {
    if (savedVersion === null) return;
    simulateVersion.mutate(
      { scenarioId: scenario.id, version: savedVersion, runs: 5 },
      {
        onSuccess: setSimulateResult,
        onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось запустить симуляцию'),
      },
    );
  }

  function runActivate() {
    if (savedVersion === null) return;
    setActivateError(null);
    activateVersion.mutate(
      { scenarioId: scenario.id, version: savedVersion },
      {
        onSuccess: () => toast.push('Версия активирована', 'success'),
        onError: (err) => {
          if (err instanceof ApiError && err.code === 'simulation_required') {
            setActivateError(err.message);
          } else {
            toast.push(err instanceof Error ? err.message : 'Не удалось активировать версию');
          }
        },
      },
    );
  }

  function runDisableActive() {
    const active = scenario.versions.find((v) => v.status === 'active');
    if (!active) return;
    disableVersion.mutate({ scenarioId: scenario.id, version: active.version });
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Сценарий: {scenario.name}</h2>
        <div className="flex gap-1">
          {scenario.versions.map((v) => (
            <Badge key={v.id} tone={v.status === 'active' ? 'success' : 'neutral'}>
              v{v.version} · {v.status}
            </Badge>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-fg">
          {advanced ? 'Полный JSON сценария' : 'Поля сценария: персона, факты, правила, стиль, этапы'}
        </div>
        <Button variant="secondary" size="sm" onClick={() => setAdvanced((v) => !v)}>
          {advanced ? 'Обычный режим' : 'Расширенный режим (JSON)'}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          {advanced ? (
            <>
              <label className="mb-1 block text-xs font-medium text-muted-fg" htmlFor="scenario-json">
                Определение сценария (JSON)
              </label>
              <textarea
                id="scenario-json"
                className="h-72 w-full rounded border border-border bg-bg p-2 font-mono text-xs"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              {!draft.ok && <div className="mt-1 text-xs text-red-600">Ошибка JSON: {draft.error}</div>}
            </>
          ) : draft.ok ? (
            <div className="max-h-[32rem] overflow-y-auto pr-2">
              <ScenarioForm
                draft={draft.definition}
                onChange={(next) => setText(JSON.stringify(next, null, 2))}
              />
            </div>
          ) : (
            <div className="rounded border border-red-300 p-3 text-xs text-red-600">
              Определение не разбирается как JSON ({draft.error}). Откройте расширенный режим и исправьте его.
            </div>
          )}
        </div>
        <div>
          <div className="mb-1 text-xs font-medium text-muted-fg">Граф этапов</div>
          <div className="h-72 overflow-auto rounded border border-border bg-card p-2">
            {draft.ok ? (
              <ScenarioGraph entryStage={draft.entryStage} stages={draft.stages} />
            ) : (
              <div className="p-4 text-xs text-muted-fg">Исправьте JSON, чтобы увидеть граф</div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={saveVersion} disabled={!draft.ok || createVersion.isPending}>
          Сохранить как версию
        </Button>
        <Button variant="secondary" onClick={runValidate} disabled={savedVersion === null || validateVersion.isPending}>
          Валидировать
        </Button>
        <Button variant="secondary" onClick={runSimulate} disabled={savedVersion === null || simulateVersion.isPending}>
          Симулировать (5 прогонов)
        </Button>
        <Button onClick={runActivate} disabled={savedVersion === null || activateVersion.isPending}>
          Активировать
        </Button>
        <Button variant="ghost" onClick={runDisableActive} disabled={disableVersion.isPending}>
          Отключить активную версию
        </Button>
      </div>

      {validateResult && (
        <div className={`rounded border p-3 text-sm ${validateResult.ok ? 'border-emerald-300' : 'border-red-300'}`}>
          <div className="font-medium">{validateResult.ok ? 'Валидация пройдена' : 'Ошибки валидации'}</div>
          {!validateResult.ok && (
            <ul className="mt-1 list-disc pl-4 text-xs text-red-700">
              {validateResult.issues.map((issue, i) => (
                <li key={i}>{issue.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {simulateResult && (
        <div className="rounded border border-border p-3 text-sm">
          <div className="font-medium">
            Прогонов без нарушений: {simulateResult.clean_runs} / {simulateResult.total_runs}
          </div>
          <ul className="mt-1 space-y-1 text-xs text-muted-fg">
            {simulateResult.runs.map((r, i) => (
              <li key={i}>
                Прогон {i + 1}: цель {r.goalReached ? 'достигнута' : 'не достигнута'}
                {r.guardrailFail ? ', нарушение guardrail' : ''} ({r.turns} реплик)
              </li>
            ))}
          </ul>
        </div>
      )}

      {savedVersion !== null && cleanRuns < MIN_CLEAN_RUNS && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          Версию ещё нельзя активировать: чистых прогонов {cleanRuns} из {MIN_CLEAN_RUNS}. Запустите «Симулировать» и
          убедитесь, что прогоны прошли без нарушений guardrail.
        </div>
      )}

      {activateError && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          Активация отклонена: {activateError}
        </div>
      )}
    </div>
  );
}
